import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Loader2 } from 'lucide-react';
import { horoscopeService, PromoStatus } from '../../services/horoscopeApi';

const ui = {
  card: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.07)',
  input: 'rgba(255,255,255,0.06)',
  inputBorder: 'rgba(255,255,255,0.1)',
  label: '#9ca3af',
};

const PromotionManager: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [until, setUntil] = useState('');
  const [banner, setBanner] = useState('');

  useEffect(() => {
    horoscopeService.getPromo().then(p => {
      setEnabled(p.enabled);
      setUntil(p.until || '');
      setBanner(p.banner || '');
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await horoscopeService.updatePromo({ enabled, until: until || null, banner });
      setMsg({ type: 'success', text: 'Configurações salvas com sucesso!' });
    } catch {
      setMsg({ type: 'error', text: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="animate-spin text-white/40" size={28} />
    </div>
  );

  // Comparação por string (YYYY-MM-DD) evita o bug de fuso do new Date() em datas sem horário.
  const todayBrt = new Date().toLocaleDateString('en-CA');
  const isActive = enabled && (!until || until >= todayBrt);

  return (
    <div className="space-y-6 max-w-xl">

      {/* Status atual */}
      <div
        className="rounded-xl p-4 flex items-center gap-3"
        style={{ background: ui.card, border: `1px solid ${ui.border}` }}
      >
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ background: isActive ? '#22c55e' : '#6b7280', boxShadow: isActive ? '0 0 8px #22c55e80' : 'none' }}
        />
        <span className="text-sm text-white/70">
          Status: <span className={isActive ? 'text-green-400 font-semibold' : 'text-gray-400'}>
            {isActive ? 'Promoção ativa' : 'Promoção inativa'}
          </span>
        </span>
      </div>

      {/* Toggle */}
      <div
        className="rounded-xl p-5"
        style={{ background: ui.card, border: `1px solid ${ui.border}` }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-sm">Tarot do Dia Gratuito</p>
            <p className="text-white/40 text-xs mt-0.5">Quando ativo, o Tarot do Dia não consome token</p>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className="relative w-12 h-6 rounded-full transition-colors duration-200 flex-shrink-0"
            style={{ background: enabled ? '#7c3aed' : 'rgba(255,255,255,0.1)' }}
          >
            <span
              className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
              style={{ left: enabled ? '26px' : '2px' }}
            />
          </button>
        </div>
      </div>

      {/* Data fim */}
      <div
        className="rounded-xl p-5 space-y-3"
        style={{ background: ui.card, border: `1px solid ${ui.border}` }}
      >
        <label className="block text-xs font-medium" style={{ color: ui.label }}>
          VÁLIDO ATÉ (opcional — deixe em branco para sem prazo)
        </label>
        <input
          type="date"
          value={until}
          onChange={e => setUntil(e.target.value)}
          className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
          style={{ background: ui.input, border: `1px solid ${ui.inputBorder}`, colorScheme: 'dark' }}
        />
      </div>

      {/* Banner text */}
      <div
        className="rounded-xl p-5 space-y-3"
        style={{ background: ui.card, border: `1px solid ${ui.border}` }}
      >
        <label className="block text-xs font-medium" style={{ color: ui.label }}>
          TEXTO DO BANNER
        </label>
        <textarea
          value={banner}
          onChange={e => setBanner(e.target.value)}
          rows={3}
          placeholder="Ex: 🎁 Tarot do Dia gratuito até 15/06! Aproveite sua leitura diária."
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"
          style={{ background: ui.input, border: `1px solid ${ui.inputBorder}`, color: 'white' }}
        />

        {/* Preview */}
        {banner && (
          <div className="mt-2">
            <p className="text-xs mb-2" style={{ color: ui.label }}>PRÉVIA DO BANNER</p>
            <div
              className="rounded-xl px-4 py-3 flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, rgba(217,119,6,0.28) 0%, rgba(245,158,11,0.18) 100%)',
                border: '1px solid rgba(245,158,11,0.45)',
              }}
            >
              <Sparkles size={16} className="text-amber-400 flex-shrink-0" />
              <p className="text-amber-200 text-xs">{banner}</p>
              {until && (
                <span className="text-amber-400/70 text-xs ml-auto flex-shrink-0">
                  até {until.split('-').reverse().join('/')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Feedback */}
      {msg && (
        <p className={`text-sm ${msg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
          {msg.text}
        </p>
      )}

      {/* Save */}
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' }}
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {saving ? 'Salvando…' : 'Salvar Configurações'}
      </button>
    </div>
  );
};

export default PromotionManager;
